import { useInfiniteQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { useEffect, useMemo } from "react";
import { fetchData } from "@/utils/api-handler";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import type { CommandAgentList } from "@/types/command-agent";

interface UseCommandAgentsOptions {
    is_active?: boolean;
    search?: string;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
    /** Distinct cache bucket — e.g. sidebar vs agents grid. */
    queryKeyPrefix?: string;
    /** Set false to keep the query off entirely — e.g. on routes with no authenticated session. */
    enabled?: boolean;
}

export function useCommandAgents(options: UseCommandAgentsOptions = {}) {
    const {
        is_active,
        search,
        limit = 50,
        sort_by = "created_at",
        sort_order = "desc",
        queryKeyPrefix = "command-agents-sidebar",
        enabled: enabledOption = true,
    } = options;

    const { hasPermission } = useProtectedQuery({
        queryKey: ["_gated_list_command_agents"],
        queryFn: async () => null,
        requiredPermissions: ["LIST_COMMAND_AGENTS"],
        enabled: false,
    });

    const { ref: loadMoreRef, inView } = useInView({
        threshold: 0,
        triggerOnce: false,
    });

    const {
        data: agentsData,
        isLoading: isLoadingAgents,
        isFetching: isFetchingAgents,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery<CommandAgentList>({
        queryKey: [queryKeyPrefix, is_active, search, limit, sort_by, sort_order],
        initialPageParam: 1,
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        queryFn: async ({ pageParam }) => {
            const params = new URLSearchParams({
                page: String(pageParam),
                limit: String(limit),
                sort_by,
                sort_order,
            });
            if (is_active !== undefined) {
                params.set("is_active", String(is_active));
            }
            if (search?.trim()) {
                params.set("search", search.trim());
            }
            return fetchData(`/api/command-team/agents?${params.toString()}`);
        },
        getNextPageParam: (lastPage, pages) => {
            if (pages.length < lastPage.page_count) {
                return pages.length + 1;
            }
            return undefined;
        },
        enabled: hasPermission && enabledOption,
    });

    const allAgents = useMemo(() => {
        return agentsData?.pages.flatMap((page) => page.items) ?? [];
    }, [agentsData]);

    const totalCount = agentsData?.pages[0]?.item_count ?? allAgents.length;

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    return {
        allAgents,
        totalCount,
        isLoadingAgents,
        isFetchingAgents,
        isFetchingNextPage,
        hasNextPage,
        loadMoreRef,
    };
}

